import React, { useState, useRef } from 'react';
import { caseUtils, commonEnums } from '@codametrix/ui-common';
import {
  MedicalCodeChip,
  CMXIcon,
  Conditional
} from '@codametrix/ui-components';
import { SuggestedLookup } from '../..';
import { shouldShowResults } from '@codametrix/ui-components';
import { objectUtils } from '@codametrix/ui-common';
import CodeFieldComponent from '../code-bench-panel/code-layer/field-components/code-field-component';

const { ChargeSessionCodeType, CodeSearchType } = commonEnums;

export type MedicalCodesManagerProps = {
  codeList: any;
  codeType: CMxCommonApp.ChargeSessionCodeType;
  editMode: boolean;
  searchResults: CMxCommonApp.CodeInfo[];
  debounceTimeout: number;
  fieldDefinitions: CMxCommonApp.FieldDefinition[];
  isSharedInvoice?: boolean;
  searchResultsLookupKey?: string;
  suggestedCodes: CMxCommonApp.SuggestedCodes;
  transactionGuid?: string;
  handleCodeDelete: (ev: any) => void;
  onCodeSearch?: CMxCommonApp.CodeSearchHandler;
  onCodeSearchSelection?: CMxCommonApp.CodeSearchSelectionHandler;
  handleCodeChecked?: (ev: any) => void;
  codeDropHandler?: (codes: CMxCommonApp.ChargeSessionCode[]) => void;
  onAddAllCodes: (
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) => void;
  onRemoveAllCodes: (
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) => void;
  codeEdits?: CMxCommonApp.CodeEditInputs;
  autoFocus?: boolean;
  changedCodes?: string[];
};

export const MedicalCodesManager: React.FC<MedicalCodesManagerProps> = ({
  codeList,
  codeType,
  editMode,
  searchResults,
  debounceTimeout = 200,
  fieldDefinitions,
  isSharedInvoice = false,
  searchResultsLookupKey,
  suggestedCodes,
  transactionGuid,
  handleCodeDelete,
  onCodeSearch,
  onCodeSearchSelection,
  handleCodeChecked,
  codeDropHandler,
  onAddAllCodes,
  onRemoveAllCodes,
  codeEdits,
  autoFocus = false,
  changedCodes
}) => {
  // Refs for interacting with the Lookup component and the CodesManager container
  const lookupRef: React.RefObject<SuggestedLookup> = React.createRef();
  const managerRef = useRef<HTMLDivElement>(null);

  const isProcedureCode = codeType === ChargeSessionCodeType.PROCEDURE;

  // State for controlling visibility of the search input
  const [isSearchVisible, setIsSearchVisible] = useState(
    objectUtils.isEmpty(codeList)
  );

  // State to manage the list of codes (for reordering)
  const [codes, setCodes] = useState(codeList);

  const shouldShowSearch = (codeList) => {
    return editMode && (!isProcedureCode || objectUtils.isEmpty(codeList));
  };

  React.useEffect(() => {
    setCodes(codeList);
    setIsSearchVisible(shouldShowSearch(codeList));
  }, [codeList]);

  // Handler for clicking on the manager container (opens search if conditions are met)
  const handleManagerClick = () => {
    if (shouldShowSearch(codeList)) {
      setIsSearchVisible(true);
      if (lookupRef.current) {
        lookupRef.current.focus(); // Set focus on the Lookup input
      }
    }
  };

  // Handler to reorder codes when dragged and dropped
  const handleCodeDrop = (dragIndex: number, hoverIndex: number) => {
    const updatedCodes = [...codes];
    const [draggedCode] = updatedCodes.splice(dragIndex, 1);
    updatedCodes.splice(hoverIndex, 0, draggedCode);
    setCodes(updatedCodes);
    codeDropHandler(updatedCodes);
  };

  // Determine if the codes are draggable (based on edit mode and code type)
  const isDraggable = editMode && !isProcedureCode && codeList.length > 1;

  // Generate the view for the list of codes using CodeChip components
  const codesView = (
    <>
      {codes.map((medicalCode, idx) => {
        const isLedgerCodeChange = !!changedCodes?.includes(medicalCode.code);
        return (
        <CodeFieldComponent
          key={`${medicalCode.code}-${idx}`}
          chipProps={{
            code: medicalCode.code,
            codeType: medicalCode.codeType,
            editable: editMode,
            handleCodeDelete: handleCodeDelete,
            sequence: idx,
            onDrop: handleCodeDrop,
            isDraggable: isDraggable,
            active: medicalCode?.active,
            shared: medicalCode?.shared,
            isSharedInvoice: isSharedInvoice,
            handleCodeChecked: handleCodeChecked,
            description: medicalCode?.description,
            evidence: medicalCode?.evidence,
            codeEdits: codeEdits,
            codeProvenance: medicalCode?.codeProvenance,
            isLedgerCodeChange
          }}
        
        />
      );
    })}
    </>
  );

  // Filter field definitions based on the selected code type
  const fieldDefinition = fieldDefinitions.filter(
    (def) => def.label.toLowerCase() === codeType.toLowerCase()
  );

  return (
    <div
      className={`codes-manager ${codeType}`}
      ref={managerRef}
      onClick={handleManagerClick}>
      {/* Left section of the manager containing the code list and search */}
      <div className="codes-manager-left">
        <Conditional every={[codes?.length !== 0]}>
          <div className="code-list">{codesView}</div>
        </Conditional>
        <Conditional on={isSearchVisible}>
          <div className="code-search">
            <SuggestedLookup
              ref={lookupRef}
              actionPrefix=""
              placeholderText={
                objectUtils.isEmpty(codeList)
                  ? `Add ${codeType.toLowerCase()}`
                  : ''
              }
              debounceTimeout={debounceTimeout}
              FieldDefinitions={fieldDefinition}
              shouldShowResults={shouldShowResults}
              autoFocus={autoFocus}
              onSearch={onCodeSearch}
              onSearchSelection={onCodeSearchSelection}
              handleCodeDelete={handleCodeDelete}
              searchResults={searchResults}
              disabled={!editMode}
              searchResultsLookupKey={searchResultsLookupKey}
              actionNameForDD={
                isSharedInvoice
                  ? '~Focus code search (Shared)'
                  : '~Focus code search (General)'
              }
              suggestedCodes={suggestedCodes}
              allowCodeRemove={
                !isSharedInvoice &&
                caseUtils.getCodeTypeShortName(codeType) !== CodeSearchType.CPT
              }
              transactionGuid={transactionGuid}
              onAddAllCodes={onAddAllCodes}
              onRemoveAllCodes={onRemoveAllCodes}
            />
          </div>
        </Conditional>
      </div>

      <Conditional
        on={
          editMode &&
          (!isProcedureCode ||
            (isProcedureCode && objectUtils.isEmpty(codeList)))
        }>
        <div className="codes-manager-right">
          <CMXIcon
            svgIconName="BiomeChevronDownSVG20"
            svgIconSize="16"
            svgIconClass="select-chevron"
          />
        </div>
      </Conditional>
    </div>
  );
};
